import React, {useEffect, useState} from 'react'

import MyProfileField from './MyProfileField'
import MyProfileFieldText from './MyProfileFieldText'
import SharedData from '../../../common/SharedData'
import {getCities, getSupplierInfo, updateSupplierInfo} from '../../core/_requests'
import {IProfileInfoModel} from '../../core/_models'
import MyProfileCityField from './MyProfileCityField'
import MyProfileLocationField from './MyProfileLocationField'
import MyProfileDeliveryDurationField from './MyProfileDeliveryDurationField'
import { useIntl } from 'react-intl'
import {tenantPhoneValidationMessage} from '../../../../tenant/tenantState'

const MyProfileInfoForm = () => {
  const {formatMessage} = useIntl();
  const {currentUser, language} = SharedData()
  const [profileInfo, setProfileInfo] = useState<IProfileInfoModel>()

  const [fields, setFields] = useState<any>({
    email: {
      value: '',
      edit: false,
      changed: false,
    },
    telephone: {
      value: '',
      edit: false,
      changed: false,
      error: ''
    },
    logistics_telephone: { 
      value: '',
      edit: false,
      changed: false,
      error: ''
    },
    mobile: {
      value: '',
      edit: false,
      changed: false,
      error: ''
    },
    city: {
      value: '',
      edit: false,
      changed: false,
      label: '',
    },
    location: {
      value: '',
      edit: false,
      changed: false,
    },
    delivery_duration: { value: '',
      edit: false,
      changed: false,
      label: '' },
  })
  useEffect(() => {
    const getDate = async () => {
      const {data: data} = await getSupplierInfo(currentUser?.token, language)
      
      const info = data.supplier_info
      setProfileInfo(info)
      setFields((prev: any) => ({
        ...prev,
        email: {
          ...prev['email'],
          value: info.email,
        },
        telephone: {
          ...prev['telephone'],
          value: info.telephone,
        },
        logistics_telephone: { 
          ...prev['logistics_telephone'],
          value: info.logistics_telephone,
        },
        mobile: {
          ...prev['mobile'],
          value: info.mobile,
        },
        city: {
          ...prev['city'],
          value: info.city_id,
        },
        location: {
          ...prev['location'],
          value: info.location,
        },
        delivery_duration: { ...prev['delivery_duration'],
          value: info.delivery_duration_id,
          label: info.delivery_duration_name },
      }))
    }
    getDate()
  }, [])

  const fieldChangedAlert = (fieldName: string) => {
    setFields((prev: any) => ({
      ...prev,
      [fieldName]: {...prev[fieldName], changed: true, error: ''},
    }))
  }
  const saveField = async (fieldName: string, params?: any) => {
    try {
      const sanitizePhone = (v: any) => String(v ?? '').replace(/[^\d+]/g, '') 
      let payload: any = {}

      if (['telephone', 'logistics_telephone', 'mobile'].includes(fieldName)) {
        const error = tenantPhoneValidationMessage(fields[fieldName].value, language, fieldName === 'telephone')
        if (error) {
          setFields((prev: any) => ({
            ...prev,
            [fieldName]: {...prev[fieldName], error, changed: false},
          }))
          return false
        }
      }

      switch (fieldName) {
        case 'email':
          payload.email = fields.email.value
          break
        case 'telephone':
          payload.telephone = sanitizePhone(fields.telephone.value)
          break
        case 'logistics_telephone':
          payload.logistics_telephone = sanitizePhone(fields.logistics_telephone.value)
          break
          case 'mobile':
            payload.mobile = sanitizePhone(fields.mobile.value)
          break
        case 'city':
          payload.city_id = fields.city.value
          break
        case 'location':
          if (fields.location.value && Number(fields.location.value) > 0) {
            payload.zone_id = fields.location.value
          }
          break
          case 'delivery_duration': {
            const raw = params?.newValue ?? fields.delivery_duration.value
            const id = Number(raw)
            if (!isNaN(id) && id > 0) {
              payload.delivery_duration_id = id
            }
            break
          }
        default:
          break
      }

      const response = await updateSupplierInfo(currentUser?.token, language, payload)

      if (response.status === 200) {
        fieldChangedAlert(fieldName)
        return true
      } else {
        if (params?.oldValue != null) {
          setFields((prev: any) => ({
            ...prev,
            [fieldName]: {...prev[fieldName], value: params.oldValue},
          }))
        }
        return false
      }
    } catch (err: any) {
             const serverData = err?.response?.data
             const serverErrors = serverData?.data
             let errorMsg = ''
             if (serverErrors?.[fieldName]?.length) {
               errorMsg = serverErrors[fieldName][0]
             } else if (serverData?.message) {
               errorMsg = serverData.message
             }
           setFields((prev: any) => ({
              ...prev,
               [fieldName]: {...prev[fieldName], error: errorMsg || prev[fieldName].error, changed: false}
           }))
      if (params?.oldValue != null) {
        setFields((prev: any) => ({
          ...prev,
          [fieldName]: {...prev[fieldName], value: params.oldValue},
        }))
      }
      return false
    }
  }
  return (
    <>
      <div className='row'>
        <div className='col-sm-12 d-flex flex-column gap-7 pb-10'>
          {/* Supplier Name */}
          <MyProfileFieldText label={formatMessage({ id: "supplier_name" })} value={[currentUser?.name, profileInfo?.name].filter(Boolean).join(' - ')} />

          <MyProfileField
            setFields={setFields}
            label={formatMessage({ id: "email" })}
            field={fields.email}
            fieldName='email'
            saveField={saveField}
          />
          <MyProfileField
            setFields={setFields}
            type='tel'
            label={formatMessage({ id: "mobile" })}
            field={fields.telephone}
            fieldName='telephone'
            saveField={saveField}
          />

          <MyProfileField
            setFields={setFields}
            type='tel'
            label={formatMessage({ id: "logistics_telephone" })}
            field={fields.logistics_telephone}
            fieldName='logistics_telephone'
            saveField={saveField}
          />

          <MyProfileField 
            setFields={setFields}
            type='tel'
            label={formatMessage({ id: "user_mobile" })}
            field={fields.mobile}
            fieldName='mobile'
            saveField={saveField}
          />

          <MyProfileCityField saveField={saveField} field={fields.city} setFields={setFields} />
          <MyProfileLocationField
            cityID={fields.city.value}
            saveField={saveField}
            field={fields.location}
            setFields={setFields}
          />
          <MyProfileFieldText label={formatMessage({ id: "margin" })} value={profileInfo?.margin} />

          <MyProfileDeliveryDurationField saveField={saveField} field={fields.delivery_duration} setFields={setFields} />

          <MyProfileFieldText label={formatMessage({ id: "payment_terms" })} value={profileInfo?.payment_terms} />

          {/* <MyProfileFieldText label={formatMessage({ id: "payment_type" })} value={profileInfo?.payment_type} /> */}

          <MyProfileFieldText label={formatMessage({ id: "account_manage" })} value={profileInfo?.account_manager} />

          {/* Branches — read-only; managed by the back-office. */}
          {profileInfo?.branches && profileInfo.branches.length > 0 && (
            <div>
              <label className='fw-bold fs-6 mb-3 d-block'>
                {formatMessage({ id: "branches", defaultMessage: "Branches" })}
              </label>
              <div className='table-responsive'>
                <table className='table table-row-bordered align-middle'>
                  <thead>
                    <tr className='fw-bold text-uppercase text-muted'>
                      <th>{formatMessage({ id: "name", defaultMessage: "Name" })}</th>
                      <th>{formatMessage({ id: "telephone", defaultMessage: "Phone" })}</th>
                      <th>{formatMessage({ id: "contact_person", defaultMessage: "Contact" })}</th>
                      <th>{formatMessage({ id: "location", defaultMessage: "Location" })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {profileInfo.branches.map((branch) => (
                      <tr key={branch.branch_id}>
                        <td>{branch.name}</td>
                        <td>{branch.telephone || '-'}</td>
                        <td>{branch.contact_person || '-'}</td>
                        <td>{branch.location || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default MyProfileInfoForm
